import type { DsdEditorCorrectionItem } from "@/utils/dsd-editor/spellcheck";
import { useCallback, useEffect, useState } from "react";
import { isTypingTarget } from "../is-typing-target";

/**
 * 맞춤법 교정 탭의 선택 상태.
 * 본문 클릭·목록 클릭·이전/다음(←/→ 포함) 어느 쪽으로 고르든 onSelect 를 부른다 (합계 검증 훅과 같은 계약)
 */
export const useDsdReviewCorrectionNavigation = ({
  enabled,
  items,
  requestCorrectionScroll,
  onSelect,
}: {
  enabled: boolean;
  items: DsdEditorCorrectionItem[];
  requestCorrectionScroll: (item: DsdEditorCorrectionItem) => void;
  /** 교정 항목을 고를 때마다 — 패널 열기 등 */
  onSelect?: (item: DsdEditorCorrectionItem) => void;
}) => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setActiveItemId(null);
    }
  }, [enabled]);

  useEffect(() => {
    if (activeItemId && !items.some((item) => item.id === activeItemId)) {
      setActiveItemId(null);
    }
  }, [activeItemId, items]);

  const select = useCallback(
    (item: DsdEditorCorrectionItem) => {
      const correctionItem =
        items.find(
          (candidate) =>
            candidate.id === item.id && candidate.kind === item.kind,
        ) ?? item;

      onSelect?.(correctionItem);
      setActiveItemId(correctionItem.id);
    },
    [items, onSelect],
  );

  const clear = useCallback(() => {
    setActiveItemId(null);
  }, []);

  const navigate = useCallback(
    (direction: -1 | 1) => {
      if (items.length === 0) return;

      const currentIndex = activeItemId
        ? items.findIndex((item) => item.id === activeItemId)
        : -1;
      const nextIndex =
        currentIndex === -1
          ? direction === 1
            ? 0
            : items.length - 1
          : (currentIndex + direction + items.length) % items.length;
      const nextItem = items[nextIndex];
      if (!nextItem) return;

      onSelect?.(nextItem);
      setActiveItemId(nextItem.id);
      requestCorrectionScroll(nextItem);
    },
    [activeItemId, items, requestCorrectionScroll, onSelect],
  );

  const selectAndScroll = useCallback(
    (item: DsdEditorCorrectionItem) => {
      select(item);
      requestCorrectionScroll(item);
    },
    [requestCorrectionScroll, select],
  );

  useEffect(() => {
    if (!enabled) return;
    if (!activeItemId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (isTypingTarget(event.target)) return;

      event.preventDefault();
      navigate(event.key === "ArrowRight" ? 1 : -1);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeItemId, enabled, navigate]);

  return {
    activeItemId,
    select,
    selectAndScroll,
    clear,
    navigate,
  };
};
